import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, Stack, Text, Button } from "@mantine/core";
import { IconBrandApple, IconBrandGooglePlay, IconCheck, IconCopy } from "@tabler/icons-react";
import { AppStoreDownloadLink, GooglePlayStoreDownloadLink } from "../../lib/data";

/**
 * Invite landing page — https://nakuzisa.com/join?ref=CODE
 * The share message is just a hook + this link; this page carries everything:
 * the code (auto-copied so it survives a store install), the RIGHT store button
 * for the visitor's platform, and an "Open in app" for people who already have it.
 */
type OS = "ios" | "android" | "other";

function detectOS(): OS {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export default function Join() {
  const [params] = useSearchParams();
  const raw = params.get("ref") || params.get("code") || "";
  const code = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  const [copied, setCopied] = useState(false);
  const os = useMemo(detectOS, []);

  const appDeepLink = useMemo(
    () => (code ? `virel://join?ref=${encodeURIComponent(code)}` : "virel://"),
    [code],
  );

  // Best-effort: copy the code on load so it's already on the clipboard when the
  // user installs and opens the app (the app reads the clipboard on first launch).
  useEffect(() => {
    if (!code) return;
    navigator.clipboard?.writeText(code).catch(() => {});
  }, [code]);

  // Keep the invite page clean: hide the Tawk.to chat widget here so its
  // "How can we help?" popup can't cover the "Get the app" / "Open Virel" CTAs.
  useEffect(() => {
    const w = window as unknown as { Tawk_API?: { hideWidget?: () => void; onLoad?: () => void } };
    const hide = () => { try { w.Tawk_API?.hideWidget?.(); } catch { /* widget not ready yet */ } };
    w.Tawk_API = w.Tawk_API || {};
    w.Tawk_API.onLoad = hide;
    hide();
    const id = window.setInterval(hide, 500);
    window.setTimeout(() => window.clearInterval(id), 5000);
    return () => window.clearInterval(id);
  }, []);

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  // Force-open the app even from in-app browsers (WhatsApp / Instagram) that
  // ignore App Links / Universal Links. Android: an intent:// URL hands straight
  // to the app by package — or falls back to the Play Store if it isn't installed.
  // iOS: best-effort custom scheme, with an App Store fallback if nothing takes
  // over the page within a moment.
  const openApp = () => {
    const q = code ? `?ref=${encodeURIComponent(code)}` : "";
    if (os === "android") {
      const fallback = encodeURIComponent(GooglePlayStoreDownloadLink);
      window.location.href =
        `intent://join${q}#Intent;scheme=virel;package=com.nakuzisa.virel;S.browser_fallback_url=${fallback};end`;
      return;
    }
    if (os === "ios") {
      const start = Date.now();
      window.location.href = appDeepLink;
      window.setTimeout(() => {
        if (Date.now() - start < 1600) window.location.href = AppStoreDownloadLink;
      }, 1200);
      return;
    }
    window.location.href = appDeepLink;
  };

  const StoreButton = ({ ios }: { ios: boolean }) => (
    <a
      href={ios ? AppStoreDownloadLink : GooglePlayStoreDownloadLink}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full sm:w-auto"
    >
      <div className="flex items-center justify-center gap-4 bg-[#10B981] px-8 py-4 rounded-2xl transition-all hover:bg-[#059669] hover:scale-105">
        {ios ? (
          <IconBrandApple size={34} className="text-white" />
        ) : (
          <IconBrandGooglePlay size={34} className="text-white" />
        )}
        <div className="flex flex-col items-start">
          <span className="text-[10px] text-emerald-100 font-bold uppercase tracking-wider">
            {ios ? "Download on the" : "Get it on"}
          </span>
          <span className="text-xl font-bold text-white leading-none">
            {ios ? "App Store" : "Google Play"}
          </span>
        </div>
      </div>
    </a>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <Container size="sm">
        <Stack align="center" gap="lg">
          <img src="/icon.png" alt="Virel" className="w-24 h-24 md:w-28 md:h-28 rounded-3xl" />

          <div className="text-center space-y-2 max-w-xl">
            <h1 className="text-3xl md:text-5xl font-black text-[#10B981] tracking-tight">
              You&apos;re invited to Virel
            </h1>
            <Text size="lg" className="text-slate-600 font-medium">
              Earn from simple opportunities. Get the app and your invite is applied automatically.
            </Text>
          </div>

          {code && (
            <button
              onClick={copyCode}
              className="group flex flex-col items-center gap-1 rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-8 py-4 transition-all hover:border-emerald-400"
            >
              <span className="text-[11px] font-semibold uppercase tracking-widest text-emerald-600">
                Your invite code {copied ? "· copied ✓" : "· tap to copy"}
              </span>
              <span className="flex items-center gap-2 text-3xl md:text-4xl font-black tracking-[0.3em] text-emerald-700">
                {code}
                {copied ? <IconCheck size={22} /> : <IconCopy size={20} className="opacity-40" />}
              </span>
            </button>
          )}

          {/* ── New here → get the app (primary). Installed users normally never
              reach this page: the invite link opens the app directly via App
              Links / Universal Links. This page is the fallback for people who
              don't have Virel yet. ── */}
          <Stack align="center" gap="xs" className="w-full max-w-md">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              New here? Get the app
            </span>
            {os === "ios" ? <StoreButton ios /> : <StoreButton ios={false} />}
            {os !== "ios" && os !== "android" && <StoreButton ios />}
            <a href={os === "ios" ? GooglePlayStoreDownloadLink : AppStoreDownloadLink}
               target="_blank" rel="noopener noreferrer"
               className="text-sm font-medium text-slate-400 hover:text-slate-600">
              {os === "ios" ? "On Android instead? Get it on Google Play" : "On iPhone instead? Download on the App Store"}
            </a>
            <Text size="sm" className="text-slate-400 text-center mt-1">
              Sign up and your invite{code ? ` (${code})` : ""} applies automatically.
            </Text>
          </Stack>

          {/* ── Already have Virel → open it (secondary, best-effort scheme) ── */}
          <div className="w-full max-w-md border-t border-slate-100 pt-5 flex flex-col items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              Already have Virel?
            </span>
            <button type="button" onClick={openApp}
               className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-emerald-500 px-8 py-3 font-bold text-emerald-600 transition-all hover:bg-emerald-50">
              Open Virel
            </button>
            <Text size="xs" className="text-slate-400 text-center">
              Opens the app if it's installed — even from WhatsApp. Otherwise it takes you to the store.
            </Text>
          </div>
        </Stack>
      </Container>
    </div>
  );
}
